/**
 * Admin → Users (container): the list (live, D35: any admin's change to a user
 * or group refetches it), invite, the row actions (resend, deactivate,
 * reactivate, delete — delete and deactivate confirmed first) and a user's
 * groups.
 */
import type { InviteDeliveryWire } from '@acp/api-contract/tenancy.js';
import { useState } from 'react';
import { toast } from 'sonner';
import type { UserAction } from '../../api/admin.js';
import { useAdminGroups, useAdminUsers, useInviteUser, useSession, useSetUserGroups, useUserAction } from '../../app/hooks.js';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog.js';
import { deliveryMessage, InviteUserDialog } from '../../components/admin/InviteUserDialog.js';
import { UserGroupsDialog } from '../../components/admin/UserGroupsDialog.js';
import { UsersTable } from '../../components/admin/UsersTable.js';
import { errorText, messageOf } from './common.js';

const CONFIRM: Readonly<Record<'deactivate' | 'delete', { title: (email: string) => string; description: string; label: string }>> = {
  deactivate: {
    title: (email) => `Deactivate ${email}?`,
    description: 'They are signed out and refused everywhere at once, and dropped from the studio servers at their next sync. Their groups are kept; reactivate to restore them.',
    label: 'Deactivate',
  },
  delete: {
    title: (email) => `Delete ${email}?`,
    description: 'Their sign-in, their groups and any pending invite are removed. This cannot be undone; invite them again to bring them back.',
    label: 'Delete user',
  },
};

/** What a row action did, for its toast. */
const DONE: Readonly<Record<UserAction, string>> = { resend: 'invite sent', deactivate: 'deactivated', reactivate: 'reactivated', delete: 'deleted' };

export function UsersAdminView() {
  const users = useAdminUsers();
  const groups = useAdminGroups();
  const invite = useInviteUser();
  const action = useUserAction();
  const setGroups = useSetUserGroups();
  const selfId = useSession()?.userId ?? '';
  const [inviting, setInviting] = useState(false);
  const [sent, setSent] = useState<{ email: string; delivery: InviteDeliveryWire; expiresAt: string } | null>(null);
  const [confirming, setConfirming] = useState<{ action: 'deactivate' | 'delete'; userId: string } | null>(null);
  const [editingGroups, setEditingGroups] = useState<string | null>(null);

  const list = users.data ?? [];
  const emailOf = (userId: string) => list.find((u) => u.userId === userId)?.email ?? userId;

  const run = (kind: UserAction, userId: string): void => {
    action.mutate(
      { action: kind, userId },
      {
        onSuccess: (result) => {
          setConfirming(null);
          toast.success(result.invite === null ? `${result.user.email}: ${DONE[kind]}` : deliveryMessage(result.invite.delivery, result.user.email));
        },
        onError: (err) => {
          if (confirming === null) toast.error(messageOf(err));
        },
      },
    );
  };

  const editing = editingGroups === null ? null : list.find((u) => u.userId === editingGroups);
  return (
    <div className="grid gap-4">
      <UsersTable
        users={list}
        groups={groups.data ?? []}
        selfId={selfId}
        loading={users.isFetching}
        error={errorText(users.error)}
        busyUserId={action.isPending ? (action.variables.userId ?? null) : null}
        onInvite={() => {
          invite.reset();
          setSent(null);
          setInviting(true);
        }}
        onRetry={() => {
          void users.refetch();
        }}
        onAction={(kind, userId) => {
          if (kind === 'deactivate' || kind === 'delete') {
            action.reset();
            setConfirming({ action: kind, userId });
          } else run(kind, userId);
        }}
        onEditGroups={(userId) => {
          setGroups.reset();
          setEditingGroups(userId);
        }}
      />
      <InviteUserDialog
        open={inviting}
        groups={groups.data ?? []}
        pending={invite.isPending}
        error={errorText(invite.error)}
        sent={sent}
        onSubmit={(request) =>
          invite.mutate(request, {
            onSuccess: (result) => setSent({ email: result.user.email, delivery: result.invite.delivery, expiresAt: result.invite.expiresAt }),
          })
        }
        onOpenChange={setInviting}
      />
      <ConfirmDialog
        open={confirming !== null}
        title={confirming === null ? '' : CONFIRM[confirming.action].title(emailOf(confirming.userId))}
        description={confirming === null ? '' : CONFIRM[confirming.action].description}
        confirmLabel={confirming === null ? '' : CONFIRM[confirming.action].label}
        typeToConfirm={null}
        pending={action.isPending}
        error={errorText(action.error)}
        onConfirm={() => {
          if (confirming !== null) run(confirming.action, confirming.userId);
        }}
        onOpenChange={(open) => (open ? undefined : setConfirming(null))}
      />
      <UserGroupsDialog
        open={editing !== null && editing !== undefined}
        email={editing?.email ?? ''}
        groups={groups.data ?? []}
        current={editing?.groupIds ?? []}
        pending={setGroups.isPending}
        error={errorText(setGroups.error)}
        onSave={(groupIds) => {
          if (editing === null || editing === undefined) return;
          setGroups.mutate(
            { userId: editing.userId, from: editing.groupIds, to: groupIds },
            {
              onSuccess: () => {
                setEditingGroups(null);
                toast.success(`Groups of ${editing.email} saved`);
              },
            },
          );
        }}
        onOpenChange={(open) => (open ? undefined : setEditingGroups(null))}
      />
    </div>
  );
}
